import { FaqBlockData, QuoteBlockData, TextBlockData, TextHeadingStyle } from '@longy/common/cms'

import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../../components/ui/accordion'
import { cn } from '../../lib/utils'
import { CmsImage } from '../primitives/CmsImage'
import { RichText } from '../primitives/RichText'
import { BlockSection, BlockTitle, byOrder } from './shared'

export const TextRenderer = ({ title, body, headingStyle }: TextBlockData) => (
    <BlockSection className="flex flex-col gap-4">
        {title && (
            <h2
                className={cn(
                    'font-bold',
                    headingStyle === TextHeadingStyle.H2
                        ? 'text-xl lg:text-2xl'
                        : 'text-2xl lg:text-4xl',
                )}
            >
                {title}
            </h2>
        )}
        <RichText doc={body} className="text-base/6 lg:text-lg/7" />
    </BlockSection>
)

export const QuoteRenderer = ({
    text,
    authorName,
    authorRole,
    authorPhotoMedia,
}: QuoteBlockData) => {
    if (!text && !authorName) return null

    return (
        <BlockSection>
            <figure className="border-primary/60 flex flex-col gap-5 border-l-4 pl-6">
                {text && (
                    <blockquote className="text-xl italic leading-relaxed lg:text-2xl lg:leading-relaxed">
                        {text}
                    </blockquote>
                )}
                {(authorName || authorRole || authorPhotoMedia?.url) && (
                    <figcaption className="flex items-center gap-3">
                        <CmsImage
                            media={authorPhotoMedia}
                            sizes="56px"
                            alt={authorName || undefined}
                            className="size-14 shrink-0 rounded-full object-cover"
                        />
                        <div className="flex flex-col">
                            {authorName && (
                                <span className="text-base font-semibold">({authorName})</span>
                            )}
                            {authorRole && (
                                <span className="text-muted-foreground text-sm">{authorRole}</span>
                            )}
                        </div>
                    </figcaption>
                )}
            </figure>
        </BlockSection>
    )
}

/**
 * One answer open at a time: a FAQ is scanned by question, and several open answers push the rest
 * of the list off the screen on a phone.
 */
export const FaqRenderer = ({ title, items }: FaqBlockData) => {
    const ordered = byOrder(items)
    if (ordered.length === 0) return null

    return (
        <BlockSection className="flex flex-col gap-4">
            <BlockTitle>{title}</BlockTitle>
            <Accordion type="single" collapsible className="w-full">
                {ordered.map((item) => (
                    <AccordionItem key={item.id} value={item.id}>
                        <AccordionTrigger className="text-left text-base font-semibold lg:text-lg">
                            {item.question}
                        </AccordionTrigger>
                        <AccordionContent>
                            <RichText doc={item.answer} className="text-base/6 lg:text-lg/7" />
                        </AccordionContent>
                    </AccordionItem>
                ))}
            </Accordion>
        </BlockSection>
    )
}
